import { APP_BASE_HREF, CommonModule, Location } from '@angular/common';
import { APP_INITIALIZER, ApplicationRef, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { AppComponent, appComponentProviders } from './app.component';
import { appRouting, appRoutingProviders } from './app.routing';
import { DeactivateGuard } from './deactivate-guard';
import { ActivateGuard } from './activate-guard';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpSSOService, LMHarnessService, LMRouteComponentService } from './services';
import {
  CapitalizePipe,
  KeysPipe,
  LMBooleanAsSwitchTooltipPipe,
  LMBooleanAsTextPipe,
  LMFieldOperatorPipe,
  LMFormatLabelPipe,
  LMFormatMessagePipe,
  LMModelFieldIsBlankPipe,
  LMModelFieldToDisplayPipe,
  LMRichTextPipe,
  LMStateAsTextPipe
} from './pipes';

import { SohoComponentsModule } from 'ids-enterprise-ng';
import { LMRouteReuseStrategy } from './lm-route-resuse-strategy';

import {
  AccessDeniedErrorComponent,
  LMActionFormDialogComponent,
  LMApplicationMenuAsOfDateDialogComponent,
  LMApplicationMenuComponent,
  LMApplicationMenuHeaderComponent,
  LMApplicationMenuProxyMenuComponent,
  LMApplicationMenuSearchComponent,
  LMApplicationMenuSettingsDialogComponent,
  LMApplicationMenuSubmenuComponent,
  LMApplicationMenuUserContextDialogComponent,
  LMApplicationMenuUserContextFieldComponent,
  LMAuditLogCompareDialogComponent,
  LMAuditLogComponent,
  LMBannerAsOfDateDialogComponent,
  LMBannerHeaderComponent,
  LMBannerMastheadComponent,
  LMBannerMenuComponent,
  LMBannerNavComponent,
  LMBannerProxyMenuComponent,
  LMBannerSearchComponent,
  LMBannerUserContextDialogComponent,
  LMBannerUserContextFieldComponent,
  LMBlankLineComponent,
  LMBusclassFieldComponent,
  LMButtonComponent,
  LMCaptchaControlComponent,
  LMChartBarComponent,
  LMChartColumnComponent,
  LMChartLineComponent,
  LMChartPieComponent,
  LMChartRadarComponent,
  LMCheckboxComponent,
  LMCheckcontrolComponent,
  LMCodeBlockComponent,
  LMCodeBlockEditorComponent,
  LMColumnsDistributedComponent,
  LMColumnsGridComponent,
  LMColumnsImageFirstColumnComponent,
  LMColumnsPercentComponent,
  LMColumnsTableComponent,
  LMColumnsUndistributedComponent,
  LMCompactFormDialogComponent,
  LMComponentInfoComponent,
  LMCompoundFieldComponent,
  LMComputeBuilderComponent,
  LMComputeBuilderRowComponent,
  LMDashboardPanelCardComponent,
  LMDashboardPanelFormComponent,
  LMDashboardPanelFormTemplateComponent,
  LMDashboardPanelListComponent,
  LMDashboardPanelUrlComponent,
  LMDashboardViewComponent,
  LMDateComponent,
  LMDefaultRouteComponent,
  LMDerivedComputeBuilderComponent,
  LMDetailSectionCardComponent,
  LMDetailSectionDashBoardComponent,
  LMDetailSectionFormComponent,
  LMDetailSectionListComponent,
  LMDetailSectionMultiListComponent,
  LMDetailSectionNotFoundComponent,
  LMDetailSectionsComponent,
  LMDetailSectionUrlComponent,
  LMDirtyCheckDialogComponent,
  LMDropdownComponent,
  LMElementLayoutComponent,
  LMFieldLayoutComponent,
  LMFieldOptionsComponent,
  LMFieldPickerComponent,
  LMFieldStyleDirective,
  LMFieldUpperCaseDirective,
  LMFileuploadComponent,
  LMFilterBuilderComponent,
  LMFilterBuilderFieldLookupComponent,
  LMFilterBuilderRowComponent,
  LMFormComponent,
  LMFormContentComponent,
  LMFormContentCompositeComponent,
  LMFormContentFormComponent,
  LMFormContentInstanceCountComponent,
  LMFormContentManualRepresentationComponent,
  LMFormContentMatrixComponent,
  LMFormContentMenuComponent,
  LMFormContentSearchComponent,
  LMFormContentStepprocessComponent,
  LMFormContentTemplateComponent,
  LMFormLayoutComponent,
  LMFormPanelActionIsComponent,
  LMFormPanelDashboardComponent,
  LMFormPanelFormComponent,
  LMFormPanelIndependentFormComponent,
  LMFormPanelListComponent,
  LMFormPanelMultiListComponent,
  LMFormPanelNotFoundComponent,
  LMFormPanelTemplateComponent,
  LMFormPanelTextComponent,
  LMFormPanelUrlComponent,
  LMHeaderComponent,
  LMHelperListDialogComponent,
  LMHomePageComponent,
  LMHomeRouteComponent,
  LMHorizontalScrollerComponent,
  LMHtmlPageComponent,
  LMHtmlUrlDocComponent,
  LMHyperlinkComponent,
  LMImageComponent,
  LMKeyboardShortcutsDialogComponent,
  LMLabelModule,
  LMLayoutUtilDirective,
  LMListCalendarComponent,
  LMListCardComponent,
  LMListChartComponent,
  LMListChartSelectorsComponent,
  LMListComponent,
  LMListContentComponent,
  LMListContentFilterListComponent,
  LMListCubeViewerComponent,
  LMListGalleryComponent,
  LMListGridComponent,
  LMListHierarchyComponent,
  LMListImageComponent,
  LMBlockItemComponent,
  LMListSearchFormDialogComponent,
  LMListSearchTagsComponent,
  LMListSelectionTagsComponent,
  LMListTreeComponent,
  LMListViewComponent,
  LMLoginComponent,
  LMLookupComponent,
  LMLookupDialogComponent,
  LMManualComponent,
  LMManualRepresentationComponent,
  LMMenuFieldComponent,
  LMMouseOverTextComponent,
  LMNavigationComponent,
  LMPageComponent,
  LMPagePaneDashBoardComponent,
  LMPagePanelComponent,
  LMPagePaneListComponent,
  LMPagePaneMenuComponent,
  LMPagePaneMultiListComponent,
  LMPagePaneUrlComponent,
  LMParagraphLayoutComponent,
  LMPendingChangesDialogComponent,
  LMPreviewOptionsDialogComponent,
  LMPrintCsvDialogComponent,
  LMPrintPdfDialogComponent,
  LMProcessIndicatorComponent,
  LMProgressComponent,
  LMRadiobuttonComponent,
  LMRatingComponent,
  LMReportPersonalizationDialogComponent,
  LMResponseRequiredModalFormComponent,
  LMRichtextComponent,
  LMSavedSearchManageComponent,
  LMScanOptionsComponent,
  LMSectionedCardComponent,
  LMSelectDialogComponent,
  LMSelectRecordDialogComponent,
  LMSimpleCardComponent,
  LMSimpleProgressComponent,
  LMSubFormDialogComponent,
  LMSuiteHomeComponent,
  LMSwatchDirective,
  LMTemplateFormRecordComponent,
  LMTextareaComponent,
  LMTextComponent,
  LMTextStyleDirective,
  LMTimeComponent,
  LMToolbarComponent,
  LMTrackDirtyDirective,
  LMTwelveColumnGridComponent,
  LMTwelveColumnPanelCardComponent,
  LMTwelveColumnPanelFormComponent,
  LMTwelveColumnPanelFormTemplateComponent,
  LMTwelveColumnPanelListComponent,
  LMTwelveColumnPanelUrlComponent,
  LMViewExceptionDetailsComponent,
  LMViewMoreComponent,
  LMVisibleWhenDirective
} from './components';

import { SafePipe } from '@pipes/safeUrl.pipe';
import { LMListSearchFormDirective } from '@list/lm-list-search-form.directive';
import { LMListLookupDialogComponent } from '@list/lm-list-lookup-dialog.component';
import { LMContextService } from '@services/lm-context.service';
import { HttpCSRFInterceptorService } from '@services/http.csrf.interceptor.service';
import { LMListAuditComparePanelComponent } from '@list/lm-list-audit-compare-panel.component';
import { LMActionComponent } from '@action/lm-action.component';
import { LMLocaleInitializerModule } from '@locale/lm-locale-initializer.module';
import { DrawerModule } from '@drawer/drawer.module';
import { TransactionModule } from '@transaction/transaction.module';
import { CubesModule } from '@modal/cubes/cubes.module';
import { LMNotificationComponent } from '@notification/lm-notification.component';
import { UserWebRequestHttpInterceptor } from './http.intercepter';
import { LMHttpIntercepterService } from '@services/lm-http-intercepter.service';
import { LMLplEditorComponent } from '@lplEditor/lm-lpl-editor.component';
import { LMFieldEditorCompareComponent, LMFieldEditorComponent } from '@field/editor';
import { LMListChartAggregationComponent } from '@list/lm-list-chart-aggregation';
import { LMCubeCellRelTestComponent } from './components/modal/cube-cell-rel';
import { LMLplEditorCompareComponent } from '@lplEditor/lm-lpl-editor-compare.component';
import { LMFileUploadProgressComponent } from '@services/file-upload/lm-file-upload-progress.component';
import { LMProcessIndicatorTooltipPipe } from './components/field/processindicator/lm-process-indicator-tooltip.pipe';
import { LMCommonModule } from '@app/lm-common.module';
import { LMNewThemeHoverDirective } from '../directives/lm-new-theme-hover.directive';
import { LMSavedSearchComponent } from './components/modal/saved-search/lm-saved-search.component';
import { LMPollAndPrintDialogComponent } from '@modal/print-dialog/lm-poll-and-print-dialog.component';
import { LMListDropdownComponent } from '@components/lmlistdropdown';
import { LMBlockGridComponent } from '@components/blockgrid';
import { LMRepositoryHelperComponent } from '@lplEditor/lm-repository-helper.component';


// eslint-disable-next-line prefer-arrow/prefer-arrow-functions,@typescript-eslint/ban-types
export function contextServiceFactory(contextService: LMContextService): Function {
  return () => contextService.setContext(LandmarkContext);
}

@NgModule({
  imports: [
    // angular and 3rd party stuff
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'SSO.CSRF',
      headerName: 'SSO.CSRF',
    }),
    BrowserModule,
    LMLocaleInitializerModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    BrowserAnimationsModule,
    appRouting,
    LMLabelModule,
    // soho imports down here
    SohoComponentsModule,
    DrawerModule,
    TransactionModule,
    LMCommonModule,
    CubesModule,
  ],
  declarations: [
    AppComponent,
    // Application Menu (previously lm-banner)
    LMApplicationMenuAsOfDateDialogComponent,
    LMApplicationMenuHeaderComponent,
    LMApplicationMenuProxyMenuComponent,
    LMApplicationMenuSearchComponent,
    LMApplicationMenuSettingsDialogComponent,
    LMApplicationMenuSubmenuComponent,
    LMApplicationMenuUserContextDialogComponent,
    LMApplicationMenuUserContextFieldComponent,
    LMApplicationMenuComponent,
    LMHtmlPageComponent,
    LMHtmlUrlDocComponent,
    LMHomePageComponent,
    LMSimpleCardComponent,
    LMSuiteHomeComponent,
    AccessDeniedErrorComponent,
    LMVisibleWhenDirective,
    // form stuff
    LMColumnsDistributedComponent,
    LMColumnsGridComponent,
    LMColumnsUndistributedComponent,
    LMColumnsPercentComponent,
    LMColumnsTableComponent,
    LMColumnsImageFirstColumnComponent,
    LMFormContentCompositeComponent,
    LMFormPanelActionIsComponent,
    LMFormPanelDashboardComponent,
    LMFormPanelFormComponent,
    LMFormPanelUrlComponent,
    LMFormPanelIndependentFormComponent,
    LMFormContentMatrixComponent,
    LMComponentInfoComponent,
    LMFormPanelListComponent,
    LMFormPanelMultiListComponent,
    LMFormContentTemplateComponent,
    LMFormPanelNotFoundComponent,
    LMFormContentInstanceCountComponent,
    LMFormContentManualRepresentationComponent,
    LMElementLayoutComponent,
    LMFieldLayoutComponent,
    LMFormComponent,
    LMFormContentComponent,
    LMFormContentSearchComponent,
    LMFormContentFormComponent,
    LMFormLayoutComponent,
    LMLayoutUtilDirective,
    LMFormPanelTemplateComponent,
    LMTemplateFormRecordComponent,
    LMFormPanelTextComponent,
    LMHeaderComponent,
    LMParagraphLayoutComponent,
    LMFormContentStepprocessComponent,
    LMManualComponent,
    KeysPipe,
    // list stuff
    LMChartColumnComponent,
    LMChartBarComponent,
    LMChartLineComponent,
    LMChartPieComponent,
    LMChartRadarComponent,
    LMListChartAggregationComponent,
    LMCodeBlockComponent,
    LMCodeBlockEditorComponent,
    LMListChartComponent,
    LMListChartSelectorsComponent,
    LMListCalendarComponent,
    LMListComponent,
    LMListViewComponent,
    LMListContentComponent,
    LMListCubeViewerComponent,
    LMListGridComponent,
    LMListImageComponent,
    LMBlockItemComponent,
    LMDetailSectionsComponent,
    LMDetailSectionCardComponent,
    LMDetailSectionDashBoardComponent,
    LMDetailSectionFormComponent,
    LMDetailSectionListComponent,
    LMDetailSectionMultiListComponent,
    LMDetailSectionUrlComponent,
    LMDetailSectionNotFoundComponent,
    LMListSearchTagsComponent,
    LMListSelectionTagsComponent,
    LMListTreeComponent,
    LMListHierarchyComponent,
    LMListLookupDialogComponent,
    LMListSearchFormDirective,
    LMListSearchFormDialogComponent,
    LMListContentFilterListComponent,
    LMListCardComponent,
    // page stuff
    LMPageComponent,
    LMPagePaneListComponent,
    LMPagePanelComponent,
    LMPagePaneDashBoardComponent,
    LMPagePaneMenuComponent,
    LMPagePaneMultiListComponent,
    LMPagePaneUrlComponent,
    // field stuff
    LMBusclassFieldComponent,
    LMButtonComponent,
    LMBlankLineComponent,
    LMCaptchaControlComponent,
    LMCheckboxComponent,
    LMCheckcontrolComponent,
    LMCompoundFieldComponent,
    LMFileuploadComponent,
    LMDateComponent,
    LMDropdownComponent,
    LMFieldEditorComponent,
    LMFieldEditorCompareComponent,
    LMFieldOptionsComponent,
    LMScanOptionsComponent,
    LMFieldStyleDirective,
    LMFieldUpperCaseDirective,
    LMImageComponent,
    LMLookupComponent,
    LMLookupDialogComponent,
    LMManualRepresentationComponent,
    LMMenuFieldComponent,
    LMMouseOverTextComponent,
    LMRadiobuttonComponent,
    LMRatingComponent,
    LMRichtextComponent,
    LMTextComponent,
    LMTextareaComponent,
    LMTextStyleDirective,
    LMTrackDirtyDirective,
    LMTimeComponent,
    LMProgressComponent,
    LMProcessIndicatorComponent,
    LMHyperlinkComponent,
    LMSimpleProgressComponent,
    LMViewExceptionDetailsComponent,
    // toolbar stuff
    LMToolbarComponent,
    // banner stuff
    LMBannerAsOfDateDialogComponent,
    LMBannerHeaderComponent,
    LMBannerNavComponent,
    LMBannerSearchComponent,
    LMBannerMenuComponent,
    LMBannerMastheadComponent,
    LMBannerUserContextDialogComponent,
    LMBannerUserContextFieldComponent,
    LMBannerProxyMenuComponent,
    LMSwatchDirective,
    // LMLegacySubMenuComponent,
    // routes components
    LMDefaultRouteComponent,
    LMHomeRouteComponent,
    LMNavigationComponent,
    LMActionComponent,
    // modal dialog
    LMActionFormDialogComponent,
    LMCompactFormDialogComponent,
    // LMComputeEditorDialogComponent,
    // LMFieldEditorDialogComponent,
    LMHelperListDialogComponent,
    LMReportPersonalizationDialogComponent,
    LMResponseRequiredModalFormComponent,
    LMLoginComponent,
    LMDirtyCheckDialogComponent,
    LMSubFormDialogComponent,
    LMSelectRecordDialogComponent,
    LMSelectDialogComponent,
    LMPendingChangesDialogComponent,
    LMPrintCsvDialogComponent,
    LMPrintPdfDialogComponent,
    LMPollAndPrintDialogComponent,
    LMKeyboardShortcutsDialogComponent,
    // pipes
    CapitalizePipe,
    SafePipe,
    // menu
    LMFormContentMenuComponent,
    // dashboards
    LMDashboardPanelCardComponent,
    LMDashboardPanelFormComponent,
    LMDashboardPanelFormTemplateComponent,
    LMDashboardPanelListComponent,
    LMDashboardPanelUrlComponent,
    LMDashboardViewComponent,
    // twelve column grid
    LMTwelveColumnGridComponent,
    LMTwelveColumnPanelCardComponent,
    LMTwelveColumnPanelFormComponent,
    LMTwelveColumnPanelFormTemplateComponent,
    LMTwelveColumnPanelListComponent,
    LMTwelveColumnPanelUrlComponent,
    // custom
    LMViewMoreComponent,
    LMLplEditorComponent,
    LMLplEditorCompareComponent,
    LMListDropdownComponent,
    // audit log
    LMAuditLogComponent,
    LMAuditLogCompareDialogComponent,
    LMListAuditComparePanelComponent,
    // pipes
    LMBooleanAsTextPipe,
    LMBooleanAsSwitchTooltipPipe,
    LMFormatLabelPipe,
    LMFormatMessagePipe,
    LMModelFieldToDisplayPipe,
    LMModelFieldIsBlankPipe,
    LMRichTextPipe,
    LMStateAsTextPipe,
    LMFieldOperatorPipe,
    LMProcessIndicatorTooltipPipe,
    LMFilterBuilderComponent,
    LMFilterBuilderFieldLookupComponent,
    LMFieldPickerComponent,
    LMFilterBuilderRowComponent,
    LMPreviewOptionsDialogComponent,
    LMCodeBlockComponent,
    LMCodeBlockEditorComponent,
    LMNotificationComponent,
    // Cube cell relation
    LMCubeCellRelTestComponent,
    // Compute Field UI
    LMComputeBuilderComponent,
    LMComputeBuilderRowComponent,
    LMFileUploadProgressComponent,
    LMDerivedComputeBuilderComponent,
    LMNewThemeHoverDirective,
    LMRepositoryHelperComponent,
    LMSavedSearchComponent,
    LMSavedSearchManageComponent,
    LMHorizontalScrollerComponent,
    LMListGalleryComponent,
    LMSectionedCardComponent,
    LMBlockGridComponent
  ],
  providers: [
    LMContextService,
    {
      provide: APP_INITIALIZER,
      useFactory: contextServiceFactory,
      deps: [ LMContextService ],
      multi: true
    },
    appRoutingProviders,
    appComponentProviders,
    { provide: ActivateGuard, useClass: ActivateGuard },
    { provide: DeactivateGuard, useClass: DeactivateGuard },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCSRFInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpSSOService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserWebRequestHttpInterceptor,
      multi: true
    },
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: RouteReuseStrategy,
      useClass: LMRouteReuseStrategy
    },
    LMRouteComponentService,
    LMHarnessService,
    LMHttpIntercepterService,
  ],
  bootstrap: [ AppComponent ],
  exports: []
})

export class AppModule {
  constructor(private location: Location, applicationRef: ApplicationRef) {
    // if (!environment.production) {
    //
    //   const originalTick = applicationRef.tick;
    //   applicationRef.tick = function() {
    //     const  before = window.performance.now();
    //     const retValue = originalTick.apply(this, arguments);
    //     const after = window.performance.now();
    //     const runTime = after - before;
    //
    //     console.log(`CHANGE DETECTION TIME: ${runTime}`);
    //     // lmLoggerService.log(`CHANGE DETECTION TIME: ${runTime}`);
    //
    //     return retValue;
    //   };
    // }
    // console.log('START: Infor Landmark Xi');
    // tslint:disable-next-line:no-console
    // console.time('Infor Landmark Xi');

    // Allow () in the URL
    const path: string = location.path();
    location.replaceState(path.replace(/\(/g, '%28').replace(/\)/g, '%29'));
  }
}
